import { inject, Injectable, OnDestroy } from "@angular/core";
import { catchError, map, of, Subscription, timeout } from "rxjs";

import { ChatSocketConnectionService, IChatSocketLease } from "@app/shared/general/chat/services/chat-socket-connection.service";
import { AlertService, EAlertChannel } from "@kernel/tools/alerts/alerts.config";

import { CHAT_MESSAGE_QUERY_PARAM } from "../helpers/resource-chat-link.helper";
import { IResourceChatMentionNotification } from "../interfaces/resource-chat.interfaces";
import { ResourceChatRegistryService } from "./resource-chat-registry.service";

const MAX_REMEMBERED_MENTIONS = 500;
const ALERT_TARGET_RESOLUTION_TIMEOUT_MS = 5_000;

/**
 * Keeps mention alerts flowing wherever the user is in the application.
 *
 * It holds a share of the tab's single chat socket for the whole session,
 * rather than a socket of its own. Every ordinary chat and mention discussion
 * rides the same socket, registering its own conversation on it, so opening,
 * renewing or closing one of them never interrupts these alerts.
 * Re-authentication after an access-token renewal is handled by the connection
 * itself, so this service has nothing to reconnect.
 */
@Injectable({ providedIn: "root" })
export class ResourceChatMentionAlertService implements OnDestroy {
  private readonly connections = inject(ChatSocketConnectionService);
  private readonly alerts = inject(AlertService);
  private readonly registry = inject(ResourceChatRegistryService);

  private readonly lease: IChatSocketLease = this.connections.acquire();
  private subscription = new Subscription();
  private pendingLabels = new Subscription();
  private readonly seenEventIds = new Set<string>();

  constructor() {
    this.subscription.add(
      this.lease.actorChanged.subscribe(() => {
        this.seenEventIds.clear();
        this.pendingLabels.unsubscribe();
        this.pendingLabels = new Subscription();
      })
    );
    this.subscription.add(this.lease.fromEvent<IResourceChatMentionNotification>("chatMention").subscribe(event => this.present(event)));
  }

  private present(event: IResourceChatMentionNotification): void {
    if (!event.eventId || this.seenEventIds.has(event.eventId)) {
      return;
    }

    this.remember(event.eventId);

    const { resourceName, resourceId } = event.resource;
    const definition = this.registry.get(resourceName);

    if (!definition) {
      this.raise(event, "Chat");

      return;
    }

    const route = definition.route(resourceId);

    this.pendingLabels.add(
      this.registry
        .label(resourceName, resourceId)
        .pipe(
          timeout(ALERT_TARGET_RESOLUTION_TIMEOUT_MS),
          map(label => this.registry.notificationLabel(resourceName, label)),
          catchError(() => of(this.registry.notificationLabel(resourceName)))
        )
        .subscribe(conversation => this.raise(event, conversation, route))
    );
  }

  private raise(event: IResourceChatMentionNotification, conversation: string, route?: unknown[]): void {
    this.alerts.raise({
      key: `chat.mention:${event.eventId}`,
      title: "CHAT.MENTION_ALERT_TITLE",
      body: "CHAT.MENTION_ALERT_BODY",
      params: {
        conversation,
      },
      actorId: event.actorId,
      route,
      queryParams: {
        [CHAT_MESSAGE_QUERY_PARAM]: event.messageId,
      },
      via: {
        [EAlertChannel.Browser]: "always",
      },
    });
  }

  private remember(eventId: string): void {
    this.seenEventIds.add(eventId);
    if (this.seenEventIds.size <= MAX_REMEMBERED_MENTIONS) return;
    const oldest = this.seenEventIds.values().next().value as string | undefined;
    if (oldest) this.seenEventIds.delete(oldest);
  }

  public ngOnDestroy(): void {
    this.pendingLabels.unsubscribe();
    this.subscription.unsubscribe();
    this.subscription = new Subscription();
    this.lease.release();
  }
}
